import { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Camera, Loader2, Download, Film, Store, Radio } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SellerFlowSteps from "@/components/SellerFlowSteps";

const BACKGROUNDS = ["minimalist studio", "sunlit loft", "urban street", "editorial white cyclorama", "botanical garden"];
const MODELS = ["female model", "male model", "androgynous model"];
const BUILDS = ["slim", "athletic", "curve / plus-size", "petite"];
const TONES = ["fair skin", "olive skin", "medium-brown skin", "deep skin"];
const GARMENTS = [
  { value: "auto", label: "Auto-detect" },
  { value: "saree", label: "Saree" },
  { value: "lehenga", label: "Lehenga" },
  { value: "salwar", label: "Salwar / Anarkali" },
  { value: "kurta", label: "Kurta" },
  { value: "sherwani", label: "Sherwani" },
  { value: "gown", label: "Gown" },
  { value: "dress", label: "Dress" },
  { value: "top", label: "Top / blouse" },
  { value: "bottoms", label: "Bottoms" },
  { value: "outerwear", label: "Outerwear" },
  { value: "suit", label: "Suit" },
  { value: "scarf", label: "Scarf / stole" },
  { value: "footwear", label: "Footwear" },
  { value: "other", label: "Other — type it in" },
];

const intentSourceLabel = {
  seller: "from garment type",
  listing: "from your listing",
  photo: "detected from photo",
  fallback: "generic",
};

function filePathFromSrc(src) {
  const s = String(src || "");
  const marker = "/api/files/";
  const i = s.indexOf(marker);
  if (i >= 0) return decodeURIComponent(s.slice(i + marker.length).split("?")[0]);
  if (s.startsWith("http")) return "";
  return s.replace(/^\//, "");
}

export default function Photoshoot() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const loadedListingRef = useRef("");
  const [preview, setPreview] = useState(null);
  const [path, setPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [background, setBackground] = useState(BACKGROUNDS[0]);
  const [modelType, setModelType] = useState(MODELS[0]);
  const [build, setBuild] = useState(BUILDS[0]);
  const [tone, setTone] = useState(TONES[0]);
  const [garmentHint, setGarmentHint] = useState("auto");
  const [customGarment, setCustomGarment] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [frames, setFrames] = useState([]);
  const [lookbooking, setLookbooking] = useState(false);
  const [frameIdx, setFrameIdx] = useState(0);
  const [error, setError] = useState("");
  const [listingId, setListingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishingLive, setPublishingLive] = useState(false);
  const [applied, setApplied] = useState(false);
  const [intent, setIntent] = useState(null);

  useEffect(() => {
    const listing = searchParams.get("listing") || "";
    if (listing) setListingId(listing);
  }, [searchParams]);

  const modelDesc = `a ${build} ${tone} ${modelType}`;
  const shopShots = frames.length ? [frames[frameIdx], ...frames.filter((_, i) => i !== frameIdx)] : (result ? [result] : []);

  const { data: listings = [] } = useQuery({
    queryKey: ["my-products", user?.id],
    queryFn: async () => (await api.get(`/products?seller_id=${user.id}&limit=100`)).data,
    enabled: Boolean(user?.id),
  });

  const selectedListing = listings.find((p) => p.id === listingId);

  useEffect(() => {
    if (!listingId) return;
    const listing = listings.find((p) => p.id === listingId);
    const src = (listing?.images || [])[0];
    const filePath = filePathFromSrc(src);
    if (!filePath || loadedListingRef.current === listingId) return;
    loadedListingRef.current = listingId;
    setPath(filePath);
    setPreview(imgUrl(src));
  }, [listingId, listings]);

  useEffect(() => {
    if (frames.length < 2) return;
    const t = setInterval(() => setFrameIdx((i) => (i + 1) % frames.length), 900);
    return () => clearInterval(t);
  }, [frames]);

  const resolvedGarmentHint = () => {
    if (garmentHint === "other") return customGarment.trim();
    if (garmentHint === "auto") return "";
    return garmentHint;
  };

  const shootPayload = () => ({
    image_path: path,
    background,
    model_desc: modelDesc,
    garment_hint: resolvedGarmentHint(),
    product_id: listingId || undefined,
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setResult(null); setFrames([]); setError(""); setIntent(null); setApplied(false);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("folder", "garments");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPath(data.path);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (!path) return toast.error("Upload a garment photo first");
    if (garmentHint === "other" && !customGarment.trim()) {
      return toast.error("Type the garment name, or pick a type from the list");
    }
    setGenerating(true); setResult(null); setFrames([]); setError(""); setIntent(null); setApplied(false);
    try {
      const { data } = await api.post("/ai/photoshoot", shootPayload(), { timeout: 180000 });
      setResult(data.image_url);
      setIntent(data.intent || null);
      toast.success(data.intent?.label ? `Shot as ${data.intent.label}` : "Photoshoot ready");
    } catch (e) {
      const msg = apiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const lookbook = async () => {
    if (!path) return toast.error("Upload a garment photo first");
    if (garmentHint === "other" && !customGarment.trim()) {
      return toast.error("Type the garment name, or pick a type from the list");
    }
    setLookbooking(true); setResult(null); setFrames([]); setFrameIdx(0); setError(""); setIntent(null); setApplied(false);
    try {
      const { data } = await api.post("/ai/photoshoot/lookbook", shootPayload(), { timeout: 360000 });
      setFrames(data.frames || []);
      setIntent(data.intent || null);
      toast.success("Animated lookbook ready");
    } catch (e) {
      const msg = apiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLookbooking(false);
    }
  };

  const applyToListing = async () => {
    if (!shopShots.length) return toast.error("Generate a shot first");
    if (!listingId) return toast.error("Choose a listing");
    setSaving(true);
    try {
      await api.put(`/products/${listingId}/images`, { images: shopShots, prepend: true });
      setApplied(true);
      queryClient.invalidateQueries({ queryKey: ["my-products", user?.id] });
      toast.success("Studio shot is now the shop image. Publish when you are ready.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const publishListing = async () => {
    if (!listingId) return toast.error("Choose a listing");
    setPublishingLive(true);
    try {
      await api.put(`/products/${listingId}/live`, { is_live: true });
      await queryClient.invalidateQueries({ queryKey: ["my-products", user?.id] });
      toast.success("Listing is live on the shop");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishingLive(false);
    }
  };

  const busy = generating || lookbooking;

  const Picker = ({ label, value, set, options }) => (
    <div>
      <label className="text-[10px] tracking-luxe uppercase text-aura-muted">{label}</label>
      <select value={value} onChange={(e) => set(e.target.value)} data-testid={`picker-${label.toLowerCase()}`} className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface capitalize">
        {options.map((o) => <option key={o} value={o} className="capitalize">{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-aura-accent mb-2">
        <Camera className="w-3.5 h-3.5" /> AI Virtual Photoshoot
      </div>
      <h1 className="text-3xl font-semibold mb-2">AI studio</h1>
      <p className="text-sm text-aura-muted mb-6">
        Generate the model shot from your closet draft. Then you can publish the listing to the shop.
      </p>
      <SellerFlowSteps current={2} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <button onClick={() => fileRef.current?.click()} data-testid="shoot-upload-btn" className="w-full h-64 border-2 border-dashed border-aura-border hover:border-aura-accent transition-colors flex flex-col items-center justify-center gap-3 bg-aura-surface overflow-hidden rounded-lg">
            {preview ? <img src={preview} alt="Garment" className="w-full h-full object-contain" /> : (
              <>
                <Upload className="w-6 h-6 text-aura-muted" />
                <span className="text-sm text-aura-muted">Upload garment / flat lay</span>
              </>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="shoot-file-input" />
          {uploading && <div className="mt-2 text-xs text-aura-muted flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Garment</label>
              <select
                value={garmentHint}
                onChange={(e) => setGarmentHint(e.target.value)}
                data-testid="picker-garment"
                className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface"
              >
                {GARMENTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {garmentHint === "other" && (
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Type the garment</label>
                <input
                  type="text"
                  value={customGarment}
                  onChange={(e) => setCustomGarment(e.target.value)}
                  placeholder="e.g. kimono, dhoti, jumpsuit"
                  maxLength={80}
                  autoFocus
                  data-testid="photoshoot-custom-garment"
                  className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface"
                />
              </div>
            )}
            <Picker label="Backdrop" value={background} set={setBackground} options={BACKGROUNDS} />
            <Picker label="Model" value={modelType} set={setModelType} options={MODELS} />
            <Picker label="Build" value={build} set={setBuild} options={BUILDS} />
            <Picker label="Skin" value={tone} set={setTone} options={TONES} />
            {listings.length > 0 && (
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Match listing</label>
                <select
                  value={listingId}
                  onChange={(e) => setListingId(e.target.value)}
                  data-testid="photoshoot-listing"
                  className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm rounded-lg bg-aura-surface"
                >
                  <option value="">None — detect from photo</option>
                  {listings.map((p) => (
                    <option key={p.id} value={p.id}>{p.is_live ? "" : "Draft · "}{p.title}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={generate} disabled={busy || !path} data-testid="generate-photoshoot-btn" className="py-3.5 btn-accent text-sm font-medium flex items-center justify-center gap-2 rounded-lg disabled:opacity-50">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {generating ? "Generating…" : "Model shot"}
            </button>
            <button onClick={lookbook} disabled={busy || !path} data-testid="generate-lookbook-btn" className="py-3.5 border border-aura-accent text-sm font-medium flex items-center justify-center gap-2 rounded-lg hover:bg-aura-accent hover:text-white transition-colors disabled:opacity-50">
              {lookbooking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              {lookbooking ? "Filming…" : "Animated lookbook"}
            </button>
          </div>
        </div>

        <div>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-3">Result</div>
          <div className="w-full aspect-[3/4] bg-aura-sand overflow-hidden rounded-lg flex items-center justify-center" data-testid="photoshoot-result">
            {busy ? (
              <div className="flex flex-col items-center gap-3 text-aura-muted">
                <Loader2 className="w-7 h-7 animate-spin" />
                <span className="text-sm">{lookbooking ? "Generating multiple poses…" : "This can take up to a minute…"}</span>
              </div>
            ) : frames.length > 0 ? (
              <img src={imgUrl(frames[frameIdx])} alt="Lookbook" className="w-full h-full object-cover" data-testid="lookbook-frame" />
            ) : result ? (
              <img src={imgUrl(result)} alt="Photoshoot" className="w-full h-full object-cover fade-up" />
            ) : error ? (
              <span className="text-sm text-red-400 px-6 text-center">{error}</span>
            ) : (
              <span className="text-sm text-aura-muted px-6 text-center">Your editorial shot will appear here.</span>
            )}
          </div>
          {intent?.label && !busy && (
            <p className="mt-3 text-xs text-aura-muted" data-testid="photoshoot-intent">
              Worn as <span className="text-aura-ink font-medium">{intent.label}</span>
              {intentSourceLabel[intent.source] ? ` · ${intentSourceLabel[intent.source]}` : ""}
            </p>
          )}
          {frames.length > 0 && (
            <div className="mt-3 flex items-center justify-center gap-2">
              {frames.map((_, i) => (
                <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === frameIdx ? "bg-aura-accent" : "bg-aura-border"}`} />
              ))}
              <span className="ml-2 text-xs text-aura-muted">Animated lookbook · {frames.length} poses</span>
            </div>
          )}
          {shopShots.length > 0 && !busy && (
            <div className="mt-4 space-y-3">
              <a href={imgUrl(shopShots[0])} target="_blank" rel="noreferrer" className="w-full py-3 border border-aura-border text-sm flex items-center justify-center gap-2 hover:border-aura-accent transition-colors rounded-lg" data-testid="download-shot">
                <Download className="w-4 h-4" /> Open full size
              </a>
              {listings.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={applyToListing}
                    disabled={saving || !listingId}
                    data-testid="photoshoot-use-on-listing"
                    className="w-full py-2.5 px-4 btn-accent text-sm font-medium flex items-center justify-center gap-2 rounded-lg disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />}
                    {saving ? "Saving…" : listingId ? "Use as shop image" : "Pick a listing above first"}
                  </button>
                  {listingId && (
                    selectedListing?.is_live ? (
                      <Link
                        to={`/product/${listingId}`}
                        className="w-full py-2.5 px-4 border border-aura-border text-sm font-medium flex items-center justify-center gap-2 rounded-lg"
                      >
                        Live on shop
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={publishListing}
                        disabled={publishingLive || (!applied && !shopShots.length)}
                        data-testid="photoshoot-publish"
                        className="w-full py-2.5 px-4 border border-aura-accent text-sm font-medium flex items-center justify-center gap-2 rounded-lg hover:bg-aura-accent hover:text-white transition-colors disabled:opacity-50"
                      >
                        {publishingLive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
                        {publishingLive ? "Publishing…" : applied ? "Publish to shop" : "Save as shop image, then publish"}
                      </button>
                    )
                  )}
                </>
              ) : (
                <p className="text-xs text-aura-muted">
                  No listings yet.{" "}
                  <Link to="/seller/add" className="text-aura-accent hover:underline">Create one in Closet ingest</Link>
                  {" "}and generate a studio shot there, or come back to attach this image.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
